/**
 * Ageing Profiles (GAP-011) — a company's own ageing ladders, per party type.
 *
 * The report uses the default profile for each party type; with none configured it falls back to the
 * built-in bands. Bands are contiguous by construction here: each band's lower bound is derived from
 * the previous band's upper bound, and the last band is always open-ended, which is exactly what the
 * server validates. So the form cannot build a ladder that drops or double-counts money.
 */
import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plus, Trash2, Star, Save, X, Pencil } from 'lucide-react';

import { ageingProfileApi } from '@/api/ageingProfile.api';
import { usePermission }    from '@/hooks/usePermission';
import { Permissions }      from '@/constants/permissions';
import { Spinner }          from '@/components/ui/Spinner';
import { ConfirmDialog }    from '@/components/ui/ConfirmDialog';
import { cn }               from '@/utils/cn';
import type { AgeingProfileDto } from '@/types/ageingProfile.types';

type PartyType = 'CUSTOMER' | 'SUPPLIER';
type Band = { toDay: number | null; label: string };

const STANDARD_TEMPLATE: Band[] = [
  { toDay: 0,    label: 'Current' },
  { toDay: 30,   label: '1-30' },
  { toDay: 60,   label: '31-60' },
  { toDay: 90,   label: '61-90' },
  { toDay: 120,  label: '91-120' },
  { toDay: null, label: '120+' },
];

const fromDayOf = (bands: Band[], i: number) =>
  i === 0 ? 0 : (bands[i - 1].toDay ?? 0) + 1;

export const AgeingProfilesPage = () => {
  const { hasPermission } = usePermission();
  const canView   = hasPermission(Permissions.AGEING_PROFILE_VIEW);
  const canManage = hasPermission(Permissions.AGEING_PROFILE_MANAGE);

  const [partyType, setPartyType] = useState<PartyType>('CUSTOMER');
  const [profiles,  setProfiles]  = useState<AgeingProfileDto[]>([]);
  const [loading,   setLoading]   = useState(false);

  // Editor state — null when closed. id null = creating a new profile.
  const [editing, setEditing] = useState<null | {
    id: string | null; name: string; isDefault: boolean; isActive: boolean; bands: Band[];
  }>(null);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const res = await ageingProfileApi.list(partyType);
      setProfiles(res.data?.data ?? []);
    } catch (e) { console.error('Ageing profiles load failed', e); }
    finally { setLoading(false); }
  }, [canView, partyType]);

  useEffect(() => { load(); }, [load]);

  const startCreate = () =>
    setEditing({ id: null, name: '', isDefault: profiles.length === 0, isActive: true, bands: STANDARD_TEMPLATE.map(b => ({ ...b })) });

  const startEdit = (p: AgeingProfileDto) =>
    setEditing({
      id: p.id, name: p.profileName, isDefault: p.isDefault, isActive: p.isActive,
      bands: p.slabs.sort((a, b) => a.sequenceNo - b.sequenceNo).map(s => ({ toDay: s.toDay, label: s.label })),
    });

  const closeEditor = () => { setEditing(null); setError(null); };

  const setBand = (i: number, patch: Partial<Band>) => {
    if (!editing) return;
    setEditing({ ...editing, bands: editing.bands.map((b, j) => j === i ? { ...b, ...patch } : b) });
  };

  const addBand = () => {
    if (!editing) return;
    // Insert a new bounded band just before the open-ended tail.
    const bands = [...editing.bands];
    const tailIdx = bands.length - 1;
    const prevTo = tailIdx > 0 ? (bands[tailIdx - 1].toDay ?? 0) : 0;
    bands.splice(tailIdx, 0, { toDay: prevTo + 30, label: `${prevTo + 1}-${prevTo + 30}` });
    setEditing({ ...editing, bands });
  };

  const removeBand = (i: number) => {
    if (!editing || editing.bands.length <= 2) return; // keep at least one bounded + the tail
    setEditing({ ...editing, bands: editing.bands.filter((_, j) => j !== i) });
  };

  const save = async () => {
    if (!editing) return;
    setError(null);

    if (!editing.name.trim()) { setError('Give the profile a name.'); return; }
    // The tail must be open-ended; every other band needs an upper bound and a label.
    const bands = editing.bands;
    for (let i = 0; i < bands.length; i++) {
      const isLast = i === bands.length - 1;
      if (!bands[i].label.trim()) { setError(`Band ${i + 1} needs a label.`); return; }
      if (isLast && bands[i].toDay !== null) { setError('The last band must be open-ended.'); return; }
      if (!isLast && (bands[i].toDay === null || bands[i].toDay! < fromDayOf(bands, i))) {
        setError(`Band ${i + 1} needs an upper bound of at least ${fromDayOf(bands, i)}.`); return;
      }
    }

    const slabs = bands.map((b, i) => ({ fromDay: fromDayOf(bands, i), toDay: b.toDay, label: b.label.trim() }));
    setSaving(true);
    try {
      if (editing.id) {
        await ageingProfileApi.update(editing.id, { id: editing.id, profileName: editing.name.trim(), isActive: editing.isActive, slabs });
      } else {
        await ageingProfileApi.create({ partyType, profileName: editing.name.trim(), isDefault: editing.isDefault, isActive: editing.isActive, slabs });
      }
      closeEditor();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.errors?.[0]?.message ?? e?.response?.data?.message ?? 'Save failed.');
    } finally { setSaving(false); }
  };

  const setDefault = async (id: string) => { await ageingProfileApi.setDefault(id); await load(); };

  // The app's own dialog, not window.confirm — a native browser prompt reads as "the site says",
  // carries none of the app's styling, and blocks the whole tab rather than just this screen.
  const [deleting, setDeleting] = useState<AgeingProfileDto | null>(null);
  const [removing, setRemoving] = useState(false);

  const confirmRemove = async () => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await ageingProfileApi.remove(deleting.id);
      setDeleting(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.errors?.[0]?.message ?? e?.response?.data?.message ?? 'Delete failed.');
    } finally { setRemoving(false); }
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p className="text-sm">You do not have permission to view ageing profiles.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Ageing Profiles</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Configurable ageing bands. The report uses the default; unset falls back to the standard ladder.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canManage && (
              <button onClick={startCreate}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                <Plus size={14} /> New Profile
              </button>
            )}
            <button onClick={load} disabled={loading} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 transition-colors disabled:opacity-40" title="Refresh">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Party type toggle */}
        <div className="flex items-center rounded-lg overflow-hidden ring-1 ring-slate-200 dark:ring-slate-600 mt-4 w-fit">
          {(['CUSTOMER', 'SUPPLIER'] as const).map(pt => (
            <button key={pt} onClick={() => { setPartyType(pt); closeEditor(); }}
              className={cn('px-4 py-1.5 text-sm font-medium transition-colors', partyType === pt ? 'bg-blue-600 text-white' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50')}>
              {pt === 'CUSTOMER' ? 'Customers (AR)' : 'Suppliers (AP)'}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-6 py-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="md" /></div>
        ) : profiles.length === 0 && !editing ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <p className="text-sm font-medium">No profiles for this party type — the standard ladder is in use.</p>
          </div>
        ) : (
          profiles.map(p => (
            <div key={p.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm ring-1 ring-slate-200 dark:ring-slate-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900 dark:text-white">{p.profileName}</span>
                  {p.isDefault && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200"><Star size={10} /> Default</span>}
                  {!p.isActive && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500 ring-1 ring-slate-200">Inactive</span>}
                </div>
                {canManage && (
                  <div className="flex items-center gap-1">
                    {!p.isDefault && p.isActive && (
                      <button onClick={() => setDefault(p.id)} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600" title="Make default"><Star size={15} /></button>
                    )}
                    <button onClick={() => startEdit(p)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500" title="Edit"><Pencil size={15} /></button>
                    <button onClick={() => setDeleting(p)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" title="Delete"><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {p.slabs.sort((a, b) => a.sequenceNo - b.sequenceNo).map(s => (
                  <span key={s.sequenceNo} className="px-2.5 py-1 rounded-lg text-xs font-mono bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 ring-1 ring-slate-200 dark:ring-slate-600">
                    {s.label} <span className="text-slate-400">({s.fromDay}{s.toDay === null ? '+' : `–${s.toDay}`}d)</span>
                  </span>
                ))}
              </div>
            </div>
          ))
        )}

        {/* Editor */}
        {editing && (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md ring-2 ring-blue-500/40 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-900 dark:text-white">
                {editing.id ? 'Edit Profile' : `New ${partyType === 'CUSTOMER' ? 'Customer' : 'Supplier'} Profile`}
              </h2>
              <button onClick={closeEditor} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400"><X size={16} /></button>
            </div>

            <div className="flex flex-wrap items-center gap-4 mb-4">
              <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="Profile name"
                className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none min-w-[220px]" />
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
                <input type="checkbox" checked={editing.isActive} onChange={e => setEditing({ ...editing, isActive: e.target.checked })} className="accent-blue-600" /> Active
              </label>
              {!editing.id && (
                <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={editing.isDefault} onChange={e => setEditing({ ...editing, isDefault: e.target.checked })} className="accent-blue-600" /> Set as default
                </label>
              )}
            </div>

            {/* Bands */}
            <div className="space-y-2">
              {editing.bands.map((b, i) => {
                const isLast = i === editing.bands.length - 1;
                const from = fromDayOf(editing.bands, i);
                return (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="w-16 text-right font-mono text-xs text-slate-400">{from}d</span>
                    <span className="text-slate-400">→</span>
                    {isLast ? (
                      <span className="w-24 text-xs text-slate-500 font-medium">and above</span>
                    ) : (
                      <input type="number" value={b.toDay ?? ''} onChange={e => setBand(i, { toDay: e.target.value === '' ? null : Number(e.target.value) })}
                        className="w-24 text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none font-mono" />
                    )}
                    <input value={b.label} onChange={e => setBand(i, { label: e.target.value })} placeholder="Label"
                      className="flex-1 max-w-[200px] text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-1 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none" />
                    {!isLast && editing.bands.length > 2 && (
                      <button onClick={() => removeBand(i)} className="p-1 rounded hover:bg-red-50 text-red-400" title="Remove band"><Trash2 size={14} /></button>
                    )}
                  </div>
                );
              })}
            </div>

            <button onClick={addBand} className="flex items-center gap-1 mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium">
              <Plus size={13} /> Add band
            </button>

            {error && <p className="text-xs text-red-600 mt-3">{error}</p>}

            <div className="flex items-center gap-2 mt-5">
              <button onClick={save} disabled={saving || !canManage}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                <Save size={14} /> {saving ? 'Saving…' : 'Save Profile'}
              </button>
              <button onClick={closeEditor} className="px-4 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">Cancel</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleting !== null}
        variant="delete"
        title="Delete Ageing Profile"
        message={
          <span>
            Delete <strong>{deleting?.profileName}</strong>? Any report currently using it falls
            back to the company default.
          </span>
        }
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
        loading={removing}
        onConfirm={confirmRemove}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
};
